// src/data/m2/Unit7.js

export const M2_UNIT_7 = {
  id: "m2-u7",
  title: "Unit 7: Directions & GPS",
  topic: "Giving Instructions & Imperatives",
  iconType: "compass", // Icono de brújula o navegación
  grammarImage: "/m2-u7-grammar.jpg", 
  grammarText: "Para dar direcciones usamos el **Imperativo** (Verbo directo: 'Go', 'Turn'). ¡Es como ser un GPS humano!",
  
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: BASIC COMMANDS (The Imperative)
    // Enfoque: Verbos de acción directa.
    // ----------------------------------------------------------------------
    {
      id: "u23-set1",
      title: "Set 1: Basic Commands",
      questions: [
        { 
          type: "mc", 
          q: "___ (Para) right now!", 
          options: ["Stop", "Stops", "Stopping"], 
          a: "Stop",
          tip: "El imperativo usa la forma base del verbo, sin 's' ni 'ing'."
        },
        { 
          type: "mc", 
          q: "Please, ___ in.", 
          options: ["come", "go", "comes"], 
          a: "come",
          successTip: "Añadir 'Please' hace que el imperativo suene educado, no como una orden."
        },
        { 
          type: "blank", 
          q: "___ (Escucha) to me carefully.", 
          options: ["Listen"], 
          a: "Listen" 
        },
        { 
          type: "match", 
          q: "Empareja la acción:",
          pairs: [
            { left: "Sit", right: "Down (Sentarse)" },
            { left: "Stand", right: "Up (Levantarse)" },
            { left: "Wake", right: "Up (Despertar)" },
            { left: "Be", right: "Quiet (Callar)" }
          ],
          a: "completed"
        },
        { 
          type: "order", 
          q: "Ordena: Abre tu libro.", 
          words: ["Open", "your", "book,", "please."], 
          a: "Open your book, please." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: GPS MODE (Basic Directions)
    // Enfoque: Vocabulario de movimiento con soporte visual.
    // ----------------------------------------------------------------------
    {
      id: "u23-set2",
      title: "Set 2: GPS Navigation",
      questions: [
        { 
          type: "mc", 
          q: "¿Qué indica la flecha? ⬅️", 
          options: ["Turn left", "Turn right", "Go straight"], 
          a: "Turn left" // Necesitarías iconos reales o usar estos emojis como texto
        },
        { 
          type: "mc", 
          q: "Sigue todo recto: Go ___ ahead.", 
          options: ["straight", "street", "strong"], 
          a: "straight",
          tip: "Pronunciación: /streit/ (la 'gh' es muda)."
        },
        { 
          type: "match", 
          q: "Símbolos de GPS:",
          pairs: [
            { left: "⬅️", right: "Turn Left" },
            { left: "➡️", right: "Turn Right" },
            { left: "⬆️", right: "Go Straight" },
            { left: "🔄", right: "Turn Around" }
          ],
          a: "completed"
        },
        { 
          type: "input", 
          q: "Escribe la orden: 'Para' (en el semáforo).", 
          a: "stop" 
        },
        { 
          type: "order", 
          q: "Ordena: Gira a la derecha aquí.", 
          words: ["Turn", "right", "here."], 
          a: "Turn right here." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: LANDMARKS (Prepositions in Motion)
    // Enfoque: Moverse con referencia a lugares.
    // ----------------------------------------------------------------------
    {
      id: "u23-set3",
      title: "Set 3: Landmarks",
      questions: [
        { 
          type: "mc", 
          q: "Go ___ (pasa por delante) the supermarket.", 
          options: ["past", "passed", "pass"], 
          a: "past",
          tip: "La preposición de movimiento es 'Past'."
        },
        { 
          type: "blank", 
          q: "Cross the ___ (calle) carefully.", 
          options: ["street"], 
          a: "street" 
        },
        { 
          type: "mc", 
          q: "Take the ___ right.", 
          options: ["second", "two", "twice"], 
          a: "second",
          successTip: "Para indicaciones usamos números ordinales: First, Second, Third."
        },
        { 
          type: "match", 
          q: "Conecta la instrucción:",
          pairs: [
            { left: "Go past", right: "El banco (Pásalo)" },
            { left: "Cross", right: "La calle (Crúzala)" },
            { left: "Take", right: "La segunda izquierda" }
          ],
          a: "completed"
        },
        { 
          type: "order", 
          q: "Ordena: Toma la primera a la izquierda.", 
          words: ["Take", "the", "first", "left."], 
          a: "Take the first left." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: NEGATIVE IMPERATIVES (Warnings)
    // Enfoque: Prohibiciones y seguridad.
    // ----------------------------------------------------------------------
    {
      id: "u23-set4",
      title: "Set 4: Warnings & Don'ts",
      questions: [
        { 
          type: "mc", 
          q: "___ turn left here! It's one way.", 
          options: ["Don't", "No", "Not"], 
          a: "Don't",
          tip: "El imperativo negativo siempre empieza con 'Don't'."
        },
        { 
          type: "blank", 
          q: "___ (No) walk on the grass.", 
          options: ["Don't"], 
          a: "Don't" 
        },
        { 
          type: "mc", 
          q: "Please, ___ smoke inside.", 
          options: ["don't", "no", "not"], 
          a: "don't" 
        },
        { 
          type: "order", 
          q: "Ordena: No aparques aquí.", 
          words: ["Don't", "park", "here."], 
          a: "Don't park here." 
        },
        { 
          type: "input", 
          q: "Escribe la prohibición: ___ enter.", 
          a: "Don't" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: TRAFFIC & SIGNS (Vocabulary)
    // Enfoque: Entender el entorno urbano.
    // ----------------------------------------------------------------------
    {
      id: "u23-set5",
      title: "Set 5: Traffic Signs",
      questions: [
        { 
          type: "match", 
          q: "Señales de tráfico:",
          pairs: [
            { left: "Traffic Light", right: "🚥 Semáforo" },
            { left: "Crosswalk", right: "🦓 Paso de cebra" },
            { left: "Roundabout", right: "🔄 Rotonda" },
            { left: "Corner", right: "📐 Esquina" }
          ],
          a: "completed",
          successTip: "En UK 'Crosswalk' es 'Zebra crossing'. 🦓"
        },
        { 
          type: "mc", 
          q: "Wait at the ___ light.", 
          options: ["traffic", "street", "car"], 
          a: "traffic" 
        },
        { 
          type: "blank", 
          q: "Go around the ___ (rotonda).", 
          options: ["roundabout"], 
          a: "roundabout" 
        },
        { 
          type: "mc", 
          q: "It's on the ___ of Main St and 5th Ave.", 
          options: ["corner", "side"], 
          a: "corner" 
        },
        { 
          type: "order", 
          q: "Ordena: Espera en el semáforo.", 
          words: ["Wait", "at", "the", "traffic", "light."], 
          a: "Wait at the traffic light." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: ASKING FOR DIRECTIONS (Politeness)
    // Enfoque: Cómo pedir ayuda en la calle.
    // ----------------------------------------------------------------------
    {
      id: "u23-set6",
      title: "Set 6: Asking for Help",
      questions: [
        { 
          type: "mc", 
          q: "___ me, where is the bank?", 
          options: ["Excuse", "Sorry", "Forgive"], 
          a: "Excuse",
          successTip: "'Excuse me' se usa para iniciar una conversación. 'Sorry' es para disculparse."
        },
        { 
          type: "order", 
          q: "Pregunta: ¿Cómo llego al parque?", 
          words: ["How", "do", "I", "get", "to", "the", "park?"], 
          a: "How do I get to the park?",
          tip: "La estructura 'How do I get to...?' es la más común para pedir direcciones."
        },
        { 
          type: "blank", 
          q: "Can you ___ (ayudar) me?", 
          options: ["help"], 
          a: "help" 
        },
        { 
          type: "mc", 
          q: "Is it ___ (lejos)?", 
          options: ["far", "long", "distance"], 
          a: "far" 
        },
        { 
          type: "match", 
          q: "Pregunta y Respuesta:",
          pairs: [
            { left: "How do I get to...?", right: "Go straight..." },
            { left: "Is it far?", right: "No, it's near." },
            { left: "Where is...?", right: "It's on the corner." }
          ],
          a: "completed" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: ERROR CORRECTION (Grammar Police)
    // ----------------------------------------------------------------------
    {
      id: "u23-set7",
      title: "Set 7: Find the Mistake",
      questions: [
        { 
          type: "mc", 
          q: "Error: 'No turn left!'", 
          options: ["Fix: Don't turn left", "Fix: Not turn left"], 
          a: "Fix: Don't turn left",
          tip: "Imperativo negativo = Don't."
        },
        { 
          type: "mc", 
          q: "Error: 'Goes straight ahead.'", 
          options: ["Fix: Go straight", "Fix: Going straight"], 
          a: "Fix: Go straight",
          tip: "Imperativo = Verbo base (sin 'es')."
        },
        { 
          type: "input", 
          q: "Corrige: 'Takes the second right.' (Quita la 's')", 
          a: "Take the second right" 
        },
        { 
          type: "order", 
          q: "Corrige: To the park how I get?", 
          words: ["How", "do", "I", "get", "to", "the", "park?"], 
          a: "How do I get to the park?" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: MASTER CHALLENGE (Full Route)
    // Enfoque: Seguir una ruta completa compleja.
    // ----------------------------------------------------------------------
    {
      id: "u23-set8",
      title: "Set 8: The Ultimate Navigator",
      questions: [
        { 
          type: "mc", 
          q: "Route: Go straight, pass the bank, turn left. Where are you?", 
          options: ["On the street left of the bank", "Inside the bank"], 
          a: "On the street left of the bank" 
        },
        { 
          type: "order", 
          q: "Dicta la ruta: Recto, luego segunda derecha.", 
          words: ["Go", "straight,", "then", "take", "the", "second", "right."], 
          a: "Go straight, then take the second right." 
        },
        { 
          type: "input", 
          q: "Escribe: 'Cruza la calle' en inglés:", 
          a: "Cross the street" 
        },
        { 
          type: "match", 
          q: "Instrucciones avanzadas:",
          pairs: [
            { left: "Go over", right: "The bridge (El puente)" },
            { left: "Go under", right: "The bridge (El puente)" },
            { left: "Go through", right: "The tunnel (El túnel)" }
          ],
          a: "completed" 
        },
        { 
          type: "blank", 
          q: "It's on your ___ (izquierda).", 
          options: ["left"], 
          a: "left" 
        }
      ]
    }
  ]
};